import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { PracticeProgress } from "@/lib/practice-api";

/**
 * apps/web :: components/dashboard :: ProgressPanel
 *
 * Practice progress summary card on the dashboard. Auto-fetches via the
 * injected `fetcher` (same pattern as RuleDiffPanel) whenever the state
 * prop changes. Renders four pieces:
 *
 *   - Current streak (consecutive first-try correct answers, across all
 *     states — computed server-side in PR #111).
 *   - Correct-first-try ratio for the current state.
 *   - Total-attempted / total-available for the current state.
 *   - Per-category bars for categories that have ≥1 question in the bank.
 *
 * Empty-state (`total_attempted === 0`): we hide the stats and show a
 * CTA-forward panel. The Practice CTA is always visible — even when the
 * user already has history — so one-click re-entry stays obvious.
 *
 * No emojis in the source per Hard Rule #5. Lucide/phosphor is not a
 * dep of apps/web (see package.json), so we use text-only labels.
 */
export type ProgressFetcher = (state: string) => Promise<PracticeProgress>;

export interface ProgressPanelProps {
  state: string;
  fetcher: ProgressFetcher;
}

type PanelState =
  | { kind: "loading" }
  | { kind: "ok"; progress: PracticeProgress }
  | { kind: "error"; message: string };

function percent(num: number, den: number): number {
  if (den <= 0) return 0;
  return Math.round((num / den) * 100);
}

export default function ProgressPanel({ state, fetcher }: ProgressPanelProps) {
  const { t } = useTranslation();
  const [panel, setPanel] = useState<PanelState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setPanel({ kind: "loading" });
    (async () => {
      try {
        const progress = await fetcher(state);
        if (cancelled) return;
        setPanel({ kind: "ok", progress });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setPanel({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, fetcher]);

  return (
    <section
      data-testid="dashboard-progress-panel"
      aria-labelledby="dashboard-progress-heading"
      className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
    >
      <header className="flex items-center justify-between gap-sm">
        <h2
          id="dashboard-progress-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("dashboard.progress.heading")}
        </h2>
        <Link
          to="/practice"
          data-testid="progress-cta"
          className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-xs text-sm font-semibold text-white no-underline transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("dashboard.progress.cta")}
        </Link>
      </header>

      {panel.kind === "loading" && (
        <div
          role="status"
          aria-live="polite"
          data-testid="progress-loading"
          className="mt-md"
        >
          <div className="h-3 w-3/4 animate-pulse rounded bg-brand-primary/10" />
          <div className="mt-sm h-3 w-full animate-pulse rounded bg-brand-primary/10" />
        </div>
      )}

      {panel.kind === "error" && (
        <div
          role="alert"
          data-testid="progress-error"
          className="mt-md rounded-md border border-danger/40 bg-danger/5 p-md text-sm text-danger"
        >
          {t("dashboard.progress.errorPrefix")}: {panel.message}
        </div>
      )}

      {panel.kind === "ok" && panel.progress.total_attempted === 0 && (
        <div
          data-testid="progress-empty"
          className="mt-md rounded-md border border-brand-primary/10 bg-brand-surface/40 p-md text-sm text-neutral-800"
        >
          {t("dashboard.progress.empty")}
        </div>
      )}

      {panel.kind === "ok" && panel.progress.total_attempted > 0 && (
        <>
          <dl className="mt-md grid grid-cols-1 gap-md sm:grid-cols-3">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
                {t("dashboard.progress.streakLabel")}
              </dt>
              <dd
                data-testid="progress-streak"
                className="mt-xs text-2xl font-bold text-brand-primary"
              >
                {panel.progress.current_streak}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
                {t("dashboard.progress.correctFirstTryLabel")}
              </dt>
              <dd
                data-testid="progress-correct-first-try"
                className="mt-xs text-base text-brand-primary"
              >
                {t("dashboard.progress.correctFirstTryValue", {
                  correct: panel.progress.correct_first_try,
                  attempted: panel.progress.total_attempted,
                  percent: percent(
                    panel.progress.correct_first_try,
                    panel.progress.total_attempted,
                  ),
                })}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
                {t("dashboard.progress.totalLabel")}
              </dt>
              <dd
                data-testid="progress-total"
                className="mt-xs text-base text-brand-primary"
              >
                {t("dashboard.progress.totalValue", {
                  attempted: panel.progress.total_attempted,
                  available: panel.progress.total_available,
                })}
              </dd>
            </div>
          </dl>

          <ul
            aria-label={t("dashboard.progress.byCategoryAriaLabel")}
            className="mt-lg flex flex-col gap-sm"
          >
            {Object.entries(panel.progress.by_category).map(([cat, row]) => {
              const pct = percent(row.attempted, row.total);
              const label = t(`practice.category.${cat}`, {
                defaultValue: cat.replaceAll("_", " "),
              });
              return (
                <li
                  key={cat}
                  data-testid={`progress-category-${cat}`}
                  className="text-sm text-neutral-800"
                >
                  <div className="flex items-center justify-between gap-sm">
                    <span className="font-semibold capitalize">{label}</span>
                    <span className="text-xs text-neutral-600">
                      {row.attempted}/{row.total}
                    </span>
                  </div>
                  <div
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t("dashboard.progress.categoryProgressAriaLabel", {
                      category: label,
                    })}
                    className="mt-xs h-2 w-full overflow-hidden rounded-full bg-brand-primary/10"
                  >
                    <div
                      className="h-full bg-brand-primary"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
